import * as React from "react";
import ISmartScheduleShiftEntity, { WeekDays } from "sh-models/shift/ISmartScheduleShiftEntity";
import { updateRepeatOnShiftBlock } from "../../../smartSchedule/mutatorActions/actions";

const ALL_WEEKDAYS = [
    WeekDays.Monday,
    WeekDays.Tuesday,
    WeekDays.Wednesday,
    WeekDays.Thursday,
    WeekDays.Friday,
    WeekDays.Saturday,
    WeekDays.Sunday
];

interface UseWeekdaySelectionParams {
    shift: ISmartScheduleShiftEntity;
}

interface UseWeekdaySelectionReturn {
    selectedDays: WeekDays[];
    showWeekdays: boolean;
    setShowWeekdays: React.Dispatch<React.SetStateAction<boolean>>;
    toggleDay: (day: WeekDays) => void;
    handleRepeatEverydayChange: (checked: boolean) => void;
    isAllDaysSelected: boolean;
    handleWeekdayClick: (day: WeekDays, index: number) => void;
}

/**
 * Custom hook to manage weekday selection state for the ShiftBlock component
 * Handles day toggling, syncing with shift object, and "repeat everyday" functionality
 */
export const useWeekdaySelection = ({
    shift
}: UseWeekdaySelectionParams): UseWeekdaySelectionReturn => {
    const [selectedDays, setSelectedDays] = React.useState<WeekDays[]>(shift.repeatOn || []);
    const [showWeekdays, setShowWeekdays] = React.useState<boolean>(false);

    // Sync with shift object when it changes
    React.useEffect(() => {
        setSelectedDays(shift.repeatOn || []);
        if (shift.repeatOn && shift.repeatOn.length != 7) {
            setShowWeekdays(true);
        }
    }, [shift, shift.repeatOn]);

    // Check if all days are selected
    const isAllDaysSelected = React.useMemo(() => {
        return ALL_WEEKDAYS.every(day => selectedDays.includes(day));
    }, [selectedDays]);

    // Toggle individual day selection
    const toggleDay = React.useCallback((day: WeekDays) => {
        let newRepeatOn: WeekDays[] = [];
        if (selectedDays.includes(day)) {
            newRepeatOn = selectedDays.filter(d => d !== day);
        } else {
            newRepeatOn = [...selectedDays, day];
        }
        // Update both local state and store
        setSelectedDays(newRepeatOn);
        updateRepeatOnShiftBlock(shift.id, newRepeatOn);
    }, [selectedDays, shift.id]);

    // Handle "repeat everyday" checkbox change
    const handleRepeatEverydayChange = React.useCallback((checked: boolean) => {
        if (checked) {
            setSelectedDays(ALL_WEEKDAYS);
            updateRepeatOnShiftBlock(shift.id, ALL_WEEKDAYS);
        } else {
            setSelectedDays([]);
            updateRepeatOnShiftBlock(shift.id, []);
        }
        setShowWeekdays(prevShow => !prevShow);
    }, [shift.id]);

    // Handle weekday button click (combines focus management and day toggling)
    const handleWeekdayClick = React.useCallback((day: WeekDays, index: number) => {
        toggleDay(day);
    }, [toggleDay]);

    return {
        selectedDays,
        showWeekdays,
        setShowWeekdays,
        toggleDay,
        handleRepeatEverydayChange,
        isAllDaysSelected,
        handleWeekdayClick
    };
};
